import { useState, useEffect } from "react";
import { useAddNewUserMutation } from "../features/users/usersApiSlice";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import upload_icon from "../assets/images/upload_icon.png";
import CustomTooltip from "../components/ui/CustomTooltip";
import { validationColors, ROUTES } from "../assets/data";
import InputBox from "../components/InputBox";
import Modal from "../components/Modal";
import { FcGoogle } from "react-icons/fc";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Button } from "../components/ui/button";
import { useDispatch } from "react-redux";
import { setCredentials } from "../features/auth/authSlice";

const registerSchema = yup.object().shape({
  fullName: yup.string()
    .required("Full name is required")
    .min(3, "Name must be at least 3 characters")
    .max(30, "Name must be less than 30 characters")
    .matches(/^[A-Za-z ]+$/, "Name can only contain letters and spaces"),
  email: yup.string()
    .required("Email is required")
    .email("Invalid email address")
    .matches(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/, "Invalid email format"),
  phoneNumber: yup.string()
    .required("Phone number is required")
    .length(10, "Phone number must be 10 digits")
    .matches(/^[0-9]+$/, "Phone number can only contain digits"),
  password: yup.string()
    .required("Password is required")
    .min(6, "Password must be at least 6 characters")
    .max(16, "Password must be less than 16 characters")
    .matches(/^[A-Za-z0-9!@#$%]+$/, "Password can only contain letters, numbers, and !@#$%"),
  confirmPassword: yup.string()
    .required("Confirm password is required")
    .oneOf([yup.ref('password')], "Passwords must match")
});

const Register = () => {
  const [addNewUser, { isLoading, isSuccess, isError, error }] = useAddNewUserMutation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { componentHeight } = useSelector((state) => state?.general);

  const [userImage, setUserImage] = useState(null);
  const [imageKey, setImageKey] = useState(Date.now());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [step, setStep] = useState(1);
  const [showP, setShowP] = useState(false);
  const [showCP, setShowCP] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    trigger,
    control
  } = useForm({
    resolver: yupResolver(registerSchema),
    mode: "onChange",
    defaultValues: {
      fullName: "",
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: ""
    }
  });

  const formValues = watch();

  useEffect(() => {
    if (isSuccess) {
      navigate(ROUTES.HOME);
    }
  }, [isSuccess, navigate]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setModalOpen(true);
    }
  };

  const nextStep = async () => {
    if (step === 1) {
      const isValid = await trigger(["fullName"]);
      if (isValid && userImage) {
        setStep(2);
      }
    } else if (step === 2) {
      const isValid = await trigger(["email", "phoneNumber"]);
      if (isValid) {
        setStep(3);
      }
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const onSubmit = async (data) => {
    if (userImage) {
      const formData = new FormData();
      formData.append("fullName", data.fullName);
      formData.append("email", data.email);
      formData.append("phoneNumber", data.phoneNumber);
      formData.append("password", data.password);
      formData.append("userImage", userImage);

      try {
        const { accessToken } = await addNewUser(formData).unwrap();
        localStorage.setItem("persist", JSON.stringify(true));
        dispatch(setCredentials({ accessToken }));
      } catch (err) {
        console.log(err);
      }
    }
  };

  const updateProfile = async ([imgFile]) => {
    if (imgFile instanceof File) {
      setUserImage(imgFile);
    } else {
      console.error('Invalid image data received');
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedFile(null);
    const fileInput = document.getElementById('profilePicInput');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const content = (
    <div className="h-[calc(100vh-64px)] bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="bg-white p-4 rounded-lg shadow-md">
          <div className="flex justify-between mb-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                  step >= i ? 'bg-neutral-900 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {i}
                </div>
                {i < 3 && (
                  <div className={`w-10 h-0.5 ${
                    step > i ? 'bg-neutral-900' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            {step === 1 && (
              <>
                <h2 className="text-lg font-medium text-gray-800 mb-2">Create Account</h2>
                <div className="space-y-3">
                  <div className="flex flex-col items-center space-y-1">
                    <label
                      htmlFor="image-input"
                      className={`cursor-pointer ${userImage ? "bg-transparent" : "bg-neutral-300 rounded-full"}`}
                    >
                      <img
                        src={userImage ? URL.createObjectURL(userImage) : upload_icon}
                        className={`border border-gray-200 min-w-[70px] min-h-[70px] p-1 shadow-sm rounded-full w-[70px] h-[70px] ${!userImage && `p-[0px] shadow-none border border-transparent hover:border-neutral-300 transition-colors duration-150 hover:border`}`}
                        alt="upload profile pic"
                      />
                    </label>
                    <input
                      type="file"
                      id="image-input"
                      hidden
                      onChange={handleFileSelect}
                      key={imageKey}
                    />
                    <p className="text-xs text-gray-400">Upload profile picture</p>
                  </div>

                  <Controller
                    name="fullName"
                    control={control}
                    render={({ field }) => (
                      <InputBox 
                        usedFor="fullName" 
                        boxLabel="Full Name" 
                        onChange={field.onChange}
                        value={field.value}
                        error={errors.fullName?.message}
                        color="bg-white"
                      />
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Button 
                    type="button"
                    onClick={nextStep}
                    disabled={!formValues.fullName || !userImage}
                    className="w-full h-9 bg-neutral-900 text-white text-sm rounded-md transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-white text-gray-500">or continue with</span>
                    </div>
                  </div>

                  <Button 
                    type="button"
                    className="w-full h-9 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2 text-sm"
                  >
                    <FcGoogle className="w-4 h-4" />
                    <span>Sign up with Google</span>
                  </Button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <h2 className="text-lg font-medium text-gray-800 mb-2">Contact Info</h2>
                <div className="space-y-2">
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <InputBox 
                        usedFor="email" 
                        boxLabel="Email" 
                        onChange={field.onChange}
                        value={field.value}
                        error={errors.email?.message}
                        color="bg-white"
                      />
                    )}
                  />
                  <Controller
                    name="phoneNumber"
                    control={control}
                    render={({ field }) => (
                      <InputBox 
                        usedFor="phone" 
                        boxLabel="Phone Number" 
                        onChange={field.onChange}
                        value={field.value}
                        error={errors.phoneNumber?.message}
                        color="bg-white"
                      />
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <Button 
                      type="button"
                      onClick={prevStep}
                      className="w-full h-9 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors"
                    >
                      Back
                    </Button>
                    <Button 
                      type="button"
                      onClick={nextStep}
                      disabled={!formValues.email || !formValues.phoneNumber}
                      className="w-full h-9 bg-neutral-900 text-white text-sm rounded-md transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </Button>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-white text-gray-500">or continue with</span>
                    </div>
                  </div>

                  <Button 
                    type="button"
                    className="w-full h-9 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2 text-sm"
                  >
                    <FcGoogle className="w-4 h-4" />
                    <span>Sign up with Google</span>
                  </Button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <h2 className="text-lg font-medium text-gray-800 mb-2">Set Password</h2>
                <div className="space-y-2">
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <InputBox 
                        usedFor="password" 
                        boxLabel="Password" 
                        onChange={field.onChange}
                        value={field.value}
                        error={errors.password?.message}
                        typeChange={1} 
                        showPassword={showP}
                        color="bg-white"
                      >
                        <div
                          className="material-icons text-neutral-500 text-base absolute top-0 right-2 h-full flex justify-end items-center cursor-pointer border-0 bg-transparent"
                          onClick={() => setShowP(!showP)}
                        >
                          visibility
                        </div>
                      </InputBox>
                    )}
                  />
                  <Controller
                    name="confirmPassword"
                    control={control}
                    render={({ field }) => (
                      <InputBox 
                        usedFor="cPassword" 
                        boxLabel="Confirm password" 
                        onChange={field.onChange}
                        value={field.value}
                        error={errors.confirmPassword?.message}
                        typeChange={1} 
                        showPassword={showCP}
                        color="bg-white"
                      >
                        <div
                          className="material-icons text-neutral-500 text-base absolute top-0 right-2 h-full flex justify-end items-center cursor-pointer border-0 bg-transparent"
                          onClick={() => setShowCP(!showCP)}
                        >
                          visibility
                        </div>
                      </InputBox>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex space-x-2">
                    <Button 
                      type="button"
                      onClick={prevStep}
                      className="w-full h-9 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300 transition-colors"
                    >
                      Back
                    </Button>
                    <Button 
                      type="submit"
                      disabled={!isValid || !userImage}
                      className="w-full h-9 bg-neutral-900 text-white text-sm rounded-md transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Create Account
                    </Button>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-white text-gray-500">or continue with</span>
                    </div>
                  </div>

                  <Button 
                    type="button"
                    className="w-full h-9 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2 text-sm"
                  >
                    <FcGoogle className="w-4 h-4" />
                    <span>Sign up with Google</span>
                  </Button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>

      {modalOpen && selectedFile && (
        <Modal
          updateAvatar={updateProfile}
          closeModal={closeModal}
          useFor="user"
          initialImage={selectedFile}
        />
      )}
    </div>
  );

  return content;
};

export default Register;